import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { ROUTES } from "@via/utils/constants";
import { ErrorNotification } from "@via/utils/interfaces.tsx";
import {
  AcceptInvitationQueryParams,
  ApprovePendingRegistrationRequestParams,
  CancelAccountPreviewResponse,
  CancelAccountRequestBody,
  CancelInvitationRequestParams,
  CheckInviteUrlRequestParams,
  CheckInviteUrlRequestQuery,
  CheckInviteUrlResponseBody,
  GetOrganizationPendingUsersParams,
  GetOrganizationGoogleCredentialsRequestQuery,
  GetOrganizationGoogleCredentialsResponseBody,
  GetOrganizationAllowedServiceAccountsResponseBody,
  GetOrganizationRunEstimationsRequestParams,
  GetOrganizationUsersParams,
  GetOrganizationWorkbenchEstimationsResponseBody,
  CreateOrganizationGoogleCredentialRequestBody,
  CreateOrganizationGoogleCredentialResponseBody,
  UpdateOrganizationGoogleCredentialRequestBody,
  UpdateOrganizationGoogleCredentialResponseBody,
  DeleteOrganizationGoogleCredentialResponseBody,
  GoogleCredentialUsageScope,
  InviteUserToOrganizationRequestBody,
  InviteUserToOrganizationRequestParams,
  InviteUserToOrganizationRequestQuery,
  OrganizationInvitation,
  OrganizationUser,
  RemoveUserFromOrganizationRequestParams,
  RunEstimation,
  SelfSignInvitationRequestBody,
  UpdateOrganizationInvitationRequestBody,
  UpdateOrganizationInvitationRequestParams,
  UpdateOrganizationRequestBody,
  UpdateOrganizationRequestParams,
  UpdateOrganizationSettingsRequestBody,
  UpdateOrganizationSettingsRequestParams,
  UpdateOrganizationUserRequestBody,
  UpdateOrganizationUserRequestParams,
  ValidateInviteUrlResponseBody,
  GetOrganizationAllowedRoleArnsResponseBody,
  GetOrganizationAssumeRoleCredentialsResponseBody,
  CreateOrganizationAssumeRoleCredentialRequestBody,
  CreateOrganizationAssumeRoleCredentialResponseBody,
  UpdateOrganizationAssumeRoleCredentialRequestBody,
  UpdateOrganizationAssumeRoleCredentialResponseBody,
  DeleteOrganizationAssumeRoleCredentialResponseBody,
} from "common";
import { useNavigate } from "react-router-dom";
import { useLogout } from "@via/api/auth.api";

export const fetchOrganizationUsers = async (
  requestParams: GetOrganizationUsersParams
): Promise<OrganizationUser[]> => {
  try {
    const res = await axios.get(
      `/api/organization/v1/${requestParams.id}/users`
    );
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useGetOrganizationUsers = (
  requestParams: GetOrganizationUsersParams,
  options?: Omit<
    UseQueryOptions<
      OrganizationUser[],
      Error,
      OrganizationUser[],
      [string, GetOrganizationUsersParams]
    >,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: [`/api/organization/v1/${requestParams.id}/users`, requestParams],
    queryFn: () => fetchOrganizationUsers(requestParams),
    retry: false,
    ...options,
  });
};

export const fetchOrganizationPendingUsers = async (
  requestParams: GetOrganizationPendingUsersParams
): Promise<OrganizationInvitation[]> => {
  try {
    const res = await axios.get(
      `/api/organization/v1/${requestParams.id}/pending-users`
    );
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useGetOrganizationPendingUsers = (
  requestParams: GetOrganizationPendingUsersParams
) => {
  return useQuery({
    queryKey: [
      `/api/organization/v1/${requestParams.id}/pending-users`,
      requestParams,
    ],
    queryFn: () => fetchOrganizationPendingUsers(requestParams),
    retry: false,
  });
};

export const fetchOrganizationUserRoles = async (
  organizationId: string
): Promise<any> => {
  try {
    const res = await axios.get(
      `/api/organization/v1/${organizationId}/user/roles`
    );
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useGetOrganizationUserRoles = (organizationId: string) => {
  return useQuery({
    queryKey: [`/api/organization/v1/${organizationId}/user/roles`],
    queryFn: () => fetchOrganizationUserRoles(organizationId),
    retry: false,
  });
};

export const fetchInvitationCheck = async (token: string | null) => {
  try {
    if (token) {
      const res = await axios.get(
        "api/v1/organization/invitation/invite-check",
        {
          params: { token },
        }
      );
      return res.data;
    }
    return null;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useGetInvitationCheck = (token: string | null) => {
  return useQuery({
    queryKey: ["api/v1/organization/invitation/invite-check", token],
    queryFn: () => fetchInvitationCheck(token),
    retry: false,
  });
};

export const useUpdateOrganization = (): UseMutationResult<
  void,
  ErrorNotification,
  {
    requestParams: UpdateOrganizationRequestParams;
    requestBody: UpdateOrganizationRequestBody;
  }
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (payload: {
      requestParams: UpdateOrganizationRequestParams;
      requestBody: UpdateOrganizationRequestBody;
    }) => {
      await axios.patch(`/api/organization/v1/${payload.requestParams.id}`, {
        ...payload.requestBody,
        onboardingStep: payload.requestParams?.onboardingStep || undefined,
      });
    },
    onSuccess: (): void => {
      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/status"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/user"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "organization-update-failed",
        message: "Organization Update Failed",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useUpdateOrganizationSettings = (): UseMutationResult<
  void,
  ErrorNotification,
  {
    requestParams: UpdateOrganizationSettingsRequestParams;
    requestBody: UpdateOrganizationSettingsRequestBody;
  }
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (payload: {
      requestParams: UpdateOrganizationSettingsRequestParams;
      requestBody: UpdateOrganizationSettingsRequestBody;
    }) => {
      await axios.patch(
        `/api/organization/v1/${payload.requestParams.id}/settings`,
        payload.requestBody
      );
    },
    onSuccess: (): void => {
      setNotification({
        id: "invite-url-update-success",
        message: "Invite URL updated successfully",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/status"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/user"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "organization-settings-update-failed",
        message: "Organization Settings Update Failed",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

const fetchCheckInviteUrl = async (
  params: CheckInviteUrlRequestParams,
  query: CheckInviteUrlRequestQuery
): Promise<CheckInviteUrlResponseBody> => {
  const res = await axios.get(
    `/api/organization/v1/${params.organizationId}/invite-url/check`,
    { params: { slug: query.slug } }
  );
  return res.data;
};

export const useCheckInviteUrl = (
  organizationId: string,
  slug: string,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: ["/api/organization/v1/invite-url/check", organizationId, slug],
    queryFn: () => fetchCheckInviteUrl({ organizationId }, { slug }),
    enabled:
      (options?.enabled ?? true) &&
      !!organizationId &&
      !!slug &&
      slug.length >= 1 &&
      slug.length <= 255,
    retry: false,
  });
};

const fetchValidateInviteUrl = async (
  slug: string
): Promise<ValidateInviteUrlResponseBody> => {
  const res = await axios.get(
    `/api/organization/v1/public/invite-url/${encodeURIComponent(slug)}`
  );
  return res.data;
};

export const useValidateInviteUrl = (
  slug: string | undefined,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: ["/api/organization/v1/public/invite-url", slug],
    queryFn: () => fetchValidateInviteUrl(slug!),
    enabled:
      (options?.enabled ?? true) &&
      !!slug &&
      slug.length >= 1 &&
      slug.length <= 255,
    retry: false,
  });
};

export const useSelfSignInvitation = (): UseMutationResult<
  { message: string; organizationId: string },
  ErrorNotification,
  { slug: string } & SelfSignInvitationRequestBody
> => {
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async payload => {
      const { slug, ...body } = payload;
      const res = await axios.post(
        "/api/organization/v1/public/self-sign-invitation",
        body,
        { params: { slug } }
      );
      return res.data;
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "self-sign-invitation-failed",
        message: "Registration Failed",
        details:
          (error?.response?.data as { message?: string })?.message ||
          "Your registration could not be submitted. Please try again.",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useInviteUserToOrganization = (): UseMutationResult<
  any,
  ErrorNotification,
  {
    queryParams?: InviteUserToOrganizationRequestQuery;
    requestParams: InviteUserToOrganizationRequestParams;
    requestBody: InviteUserToOrganizationRequestBody;
  }
> => {
  const { setNotification, NOTIFICATION } = useNotifications();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      queryParams?: InviteUserToOrganizationRequestQuery;
      requestParams: InviteUserToOrganizationRequestParams;
      requestBody: InviteUserToOrganizationRequestBody;
    }) => {
      const response = await axios.post(
        `/api/organization/v1/${payload.requestParams.id}/invite`,
        payload.requestBody,
        {
          params: payload.queryParams?.billingConfirmed
            ? { billingConfirmed: payload.queryParams.billingConfirmed }
            : undefined,
        }
      );
      return response.data;
    },
    onSuccess: (): void => {
      queryClient.refetchQueries({
        predicate: query => {
          const queryKeyString = query.queryKey[0];
          return (
            typeof queryKeyString === "string" &&
            queryKeyString.startsWith("/api/organization/v1/")
          );
        },
      });

      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/status"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/user"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "organization-invite-failed",
        message: "Organization Invite Failed",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useInviteUserToOrganizationWithBilling = (): UseMutationResult<
  any,
  ErrorNotification,
  {
    queryParams: InviteUserToOrganizationRequestQuery;
    requestParams: InviteUserToOrganizationRequestParams;
    requestBody: InviteUserToOrganizationRequestBody;
  }
> => {
  const { setNotification, NOTIFICATION } = useNotifications();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      queryParams: InviteUserToOrganizationRequestQuery;
      requestParams: InviteUserToOrganizationRequestParams;
      requestBody: InviteUserToOrganizationRequestBody;
    }) => {
      const response = await axios.post(
        `/api/organization/v1/${payload.requestParams.id}/invite`,
        payload.requestBody,
        {
          params: { billingConfirmed: payload.queryParams.billingConfirmed },
        }
      );
      return response.data;
    },
    onSuccess: (): void => {
      queryClient.refetchQueries({
        predicate: query => {
          const queryKeyString = query.queryKey[0];
          return (
            typeof queryKeyString === "string" &&
            queryKeyString.startsWith("/api/organization/v1/")
          );
        },
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/status"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/user"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "organization-invite-billing-failed",
        message: "Organization Invite Billing Failed",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useCancelInvitation = (): UseMutationResult<
  void,
  ErrorNotification,
  CancelInvitationRequestParams
> => {
  const { setNotification, NOTIFICATION } = useNotifications();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CancelInvitationRequestParams) => {
      await axios.delete(
        `/api/organization/v1/${payload.organizationId}/invite/${payload.invitationId}/cancel`
      );
    },
    onSuccess: (): void => {
      queryClient.refetchQueries({
        predicate: query => {
          const queryKeyString = query.queryKey[0];
          return (
            typeof queryKeyString === "string" &&
            queryKeyString.startsWith("/api/organization/v1/")
          );
        },
      });

      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/status"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/user"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "organization-invite-cancel-failed",
        message: "Organization Invite Cancel Failed",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useApprovePendingRegistration = (): UseMutationResult<
  void,
  ErrorNotification,
  ApprovePendingRegistrationRequestParams
> => {
  const { setNotification, NOTIFICATION } = useNotifications();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ApprovePendingRegistrationRequestParams) => {
      await axios.post(
        `/api/organization/v1/${payload.organizationId}/pending-users/${payload.invitationId}/approve`
      );
    },
    onSuccess: (): void => {
      setNotification({
        id: "organization-approve-pending-registration-success",
        message: "Registration approved",
        details: "The user has been approved and can now sign in.",
        variant: NOTIFICATION.SUCCESS,
      });

      queryClient.refetchQueries({
        predicate: query => {
          const queryKeyString = query.queryKey[0];
          return (
            typeof queryKeyString === "string" &&
            queryKeyString.startsWith("/api/organization/v1/")
          );
        },
      });

      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/status"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/user"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "organization-approve-pending-registration-failed",
        message: "Approve Pending Registration Failed",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useUpdateInvitation = (): UseMutationResult<
  void,
  ErrorNotification,
  {
    requestParams: UpdateOrganizationInvitationRequestParams;
    requestBody: UpdateOrganizationInvitationRequestBody;
  }
> => {
  const { setNotification, NOTIFICATION } = useNotifications();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      requestParams: UpdateOrganizationInvitationRequestParams;
      requestBody: UpdateOrganizationInvitationRequestBody;
    }) => {
      await axios.patch(
        `/api/organization/v1/${payload.requestParams.id}/invitation`,
        {
          ...payload.requestBody,
        }
      );
    },
    onSuccess: (): void => {
      queryClient.refetchQueries({
        predicate: query => {
          const queryKeyString = query.queryKey[0];
          return (
            typeof queryKeyString === "string" &&
            queryKeyString.startsWith("/api/organization/v1/")
          );
        },
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "organization-invite-update-failed",
        message: "Organization Invite Update Failed",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useInviteExistingUserToOrganization = (): UseMutationResult<
  void,
  ErrorNotification,
  AcceptInvitationQueryParams
> => {
  const { setNotification, NOTIFICATION } = useNotifications();
  const { mutate: logout } = useLogout();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async payload => {
      await axios.post("/api/organization/v1/invitation/accept", undefined, {
        params: { token: payload.token },
      });
    },
    onSuccess: (): void => {
      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/status"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/user"],
      });
      navigate(ROUTES.ACTIVITY);
    },
    onError: (error: ErrorNotification) => {
      logout();
      setNotification({
        id: "organization-invitation-accept-failed",
        message: "Organization Invitation Accept Failed",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useRemoveUserFromOrganization = (): UseMutationResult<
  void,
  ErrorNotification,
  RemoveUserFromOrganizationRequestParams
> => {
  const { setNotification, NOTIFICATION } = useNotifications();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: RemoveUserFromOrganizationRequestParams) => {
      await axios.delete(
        `/api/organization/v1/${payload.organizationId}/user/${payload.userId}`
      );
    },
    onSuccess: (): void => {
      queryClient.refetchQueries({
        predicate: query => {
          const queryKeyString = query.queryKey[0];
          return (
            typeof queryKeyString === "string" &&
            queryKeyString.startsWith("/api/organization/v1/")
          );
        },
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/status"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/user"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "organization-remove-user-failed",
        message: "Removing User From Organization Failed",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useUpdateOrganizationUser = (): UseMutationResult<
  void,
  ErrorNotification,
  {
    requestParams: UpdateOrganizationUserRequestParams;
    requestBody: UpdateOrganizationUserRequestBody;
  }
> => {
  const { setNotification, NOTIFICATION } = useNotifications();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      requestParams: UpdateOrganizationUserRequestParams;
      requestBody: UpdateOrganizationUserRequestBody;
    }) => {
      await axios.patch(
        `/api/organization/v1/${payload.requestParams.id}/user`,
        {
          ...payload.requestBody,
        }
      );
    },
    onSuccess: (): void => {
      queryClient.refetchQueries({
        predicate: query => {
          const queryKeyString = query.queryKey[0];
          return (
            typeof queryKeyString === "string" &&
            queryKeyString.startsWith("/api/organization/v1/")
          );
        },
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/status"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/user"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "organization-user-update-failed",
        message: "Organization User Update Failed",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

// The organization cancel-account would act on, resolved by the server (#1136). The
// deactivate screen must not guess it from userOrganizations, which is unordered.
export const useGetCancelAccountPreview = () => {
  return useQuery({
    queryKey: ["/api/organization/v1/cancel-account/preview"],
    queryFn: async (): Promise<CancelAccountPreviewResponse> => {
      const res = await axios.get(
        "/api/organization/v1/cancel-account/preview"
      );
      return res.data;
    },
    retry: false,
  });
};

export const useCancelAccount = (): UseMutationResult<
  void,
  ErrorNotification,
  CancelAccountRequestBody
> => {
  const { setNotification, NOTIFICATION } = useNotifications();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation<void, ErrorNotification, CancelAccountRequestBody>({
    mutationFn: async (payload: CancelAccountRequestBody) => {
      await axios.post("/api/organization/v1/cancel-account", payload);
    },
    onSuccess: (_, payload: CancelAccountRequestBody): void => {
      setNotification({
        id: "deactivate-account-success",
        message: "Account Deactivated Successfully",
        details: payload.deactivateOrganization
          ? "Your organization has been deactivated and your account has been deactivated successfully. You will no longer have access to the platform."
          : "Your account has been deactivated successfully. You will no longer have access to the platform.",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/status"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/user"],
      });
      navigate(ROUTES.LOGIN);
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "deactivate-account-failed",
        message: "Deactivate Account Failed",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const fetchOrganizationRunEstimations = async (
  payload: GetOrganizationRunEstimationsRequestParams
): Promise<RunEstimation[]> => {
  try {
    const res = await axios.get(
      `/api/organization/v1/${payload.id}/run-estimations`
    );
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useGetOrganizationRunEstimations = (
  payload: GetOrganizationRunEstimationsRequestParams
) => {
  return useQuery({
    queryKey: ["/api/organization/v1/run-estimations", payload],
    queryFn: () => fetchOrganizationRunEstimations(payload),
    retry: false,
  });
};

export const fetchOrganizationWorkbenchEstimations = async (
  id: string
): Promise<GetOrganizationWorkbenchEstimationsResponseBody> => {
  try {
    const res = await axios.get(
      `/api/organization/v1/${id}/workbench-estimation`
    );
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useGetOrganizationWorkbenchEstimations = (id: string) => {
  return useQuery({
    queryKey: ["/api/organization/v1/workbench-estimation", id],
    queryFn: () => fetchOrganizationWorkbenchEstimations(id),
    retry: false,
  });
};

export const fetchOrganizationGoogleCredentials = async (params: {
  organizationId: string;
  query: GetOrganizationGoogleCredentialsRequestQuery;
}): Promise<GetOrganizationGoogleCredentialsResponseBody> => {
  const res = await axios.get(
    `/api/organization/v1/${params.organizationId}/google-credentials`,
    { params: params.query }
  );
  return res.data;
};

export const fetchOrganizationAllowedServiceAccounts = async (params: {
  organizationId: string;
}): Promise<GetOrganizationAllowedServiceAccountsResponseBody> => {
  const res = await axios.get(
    `/api/organization/v1/${params.organizationId}/allowed-service-accounts`
  );
  return res.data;
};

export const useGetOrganizationAllowedServiceAccounts = (
  organizationId: string,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: [
      `/api/organization/v1/${organizationId}/allowed-service-accounts`,
    ],
    queryFn: () => fetchOrganizationAllowedServiceAccounts({ organizationId }),
    enabled: (options?.enabled ?? true) && !!organizationId,
    retry: false,
  });
};

export const useGetOrganizationGoogleCredentials = (
  organizationId: string,
  query: GetOrganizationGoogleCredentialsRequestQuery,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: [
      `/api/organization/v1/${organizationId}/google-credentials`,
      query,
    ],
    queryFn: () =>
      fetchOrganizationGoogleCredentials({ organizationId, query }),
    enabled: (options?.enabled ?? true) && !!organizationId,
    retry: false,
  });
};

export const useCreateOrganizationGoogleCredential = (): UseMutationResult<
  CreateOrganizationGoogleCredentialResponseBody,
  ErrorNotification,
  {
    organizationId: string;
    requestBody: CreateOrganizationGoogleCredentialRequestBody;
  }
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async payload => {
      const res = await axios.post(
        `/api/organization/v1/${payload.organizationId}/google-credentials`,
        payload.requestBody
      );
      return res.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          `/api/organization/v1/${variables.organizationId}/google-credentials`,
        ],
      });
      setNotification({
        id: "org-google-credential-create-success",
        message: "Service account added",
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "org-google-credential-create-failed",
        message: "Failed to add service account",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useUpdateOrganizationGoogleCredential = (): UseMutationResult<
  UpdateOrganizationGoogleCredentialResponseBody,
  ErrorNotification,
  {
    organizationId: string;
    credentialId: number;
    requestBody: UpdateOrganizationGoogleCredentialRequestBody;
  }
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async payload => {
      const res = await axios.patch(
        `/api/organization/v1/${payload.organizationId}/google-credentials/${payload.credentialId}`,
        payload.requestBody
      );
      return res.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          `/api/organization/v1/${variables.organizationId}/google-credentials`,
        ],
      });
      setNotification({
        id: "org-google-credential-update-success",
        message: "Service account updated",
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "org-google-credential-update-failed",
        message: "Failed to update service account",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useDeleteOrganizationGoogleCredential = (): UseMutationResult<
  DeleteOrganizationGoogleCredentialResponseBody,
  ErrorNotification,
  {
    organizationId: string;
    credentialId: number;
  }
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async payload => {
      const res = await axios.delete(
        `/api/organization/v1/${payload.organizationId}/google-credentials/${payload.credentialId}`
      );
      return res.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          `/api/organization/v1/${variables.organizationId}/google-credentials`,
        ],
      });
      setNotification({
        id: "org-google-credential-delete-success",
        message: "Service account deleted",
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "org-google-credential-delete-failed",
        message: "Failed to delete service account",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const fetchOrganizationAllowedRoleArns = async (params: {
  organizationId: string;
}): Promise<GetOrganizationAllowedRoleArnsResponseBody> => {
  const res = await axios.get(
    `/api/organization/v1/${params.organizationId}/allowed-role-arns`
  );
  return res.data;
};

export const useGetOrganizationAllowedRoleArns = (
  organizationId: string,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: [`/api/organization/v1/${organizationId}/allowed-role-arns`],
    queryFn: () => fetchOrganizationAllowedRoleArns({ organizationId }),
    enabled: (options?.enabled ?? true) && !!organizationId,
    retry: false,
  });
};

export const fetchOrganizationAssumeRoleCredentials = async (params: {
  organizationId: string;
}): Promise<GetOrganizationAssumeRoleCredentialsResponseBody> => {
  const res = await axios.get(
    `/api/organization/v1/${params.organizationId}/assume-role-credentials`
  );
  return res.data;
};

export const useGetOrganizationAssumeRoleCredentials = (
  organizationId: string,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: [
      `/api/organization/v1/${organizationId}/assume-role-credentials`,
    ],
    queryFn: () => fetchOrganizationAssumeRoleCredentials({ organizationId }),
    enabled: (options?.enabled ?? true) && !!organizationId,
    retry: false,
  });
};

export const useCreateOrganizationAssumeRoleCredential = (): UseMutationResult<
  CreateOrganizationAssumeRoleCredentialResponseBody,
  ErrorNotification,
  {
    organizationId: string;
    requestBody: CreateOrganizationAssumeRoleCredentialRequestBody;
  }
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async payload => {
      const res = await axios.post(
        `/api/organization/v1/${payload.organizationId}/assume-role-credentials`,
        payload.requestBody
      );
      return res.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          `/api/organization/v1/${variables.organizationId}/assume-role-credentials`,
        ],
      });
      setNotification({
        id: "org-assume-role-create-success",
        message: "Assume-role credential added",
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "org-assume-role-create-failed",
        message: "Failed to add assume-role credential",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useUpdateOrganizationAssumeRoleCredential = (): UseMutationResult<
  UpdateOrganizationAssumeRoleCredentialResponseBody,
  ErrorNotification,
  {
    organizationId: string;
    credentialId: number;
    requestBody: UpdateOrganizationAssumeRoleCredentialRequestBody;
  }
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async payload => {
      const res = await axios.patch(
        `/api/organization/v1/${payload.organizationId}/assume-role-credentials/${payload.credentialId}`,
        payload.requestBody
      );
      return res.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          `/api/organization/v1/${variables.organizationId}/assume-role-credentials`,
        ],
      });
      setNotification({
        id: "org-assume-role-update-success",
        message: "Assume-role credential updated",
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "org-assume-role-update-failed",
        message: "Failed to update assume-role credential",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useDeleteOrganizationAssumeRoleCredential = (): UseMutationResult<
  DeleteOrganizationAssumeRoleCredentialResponseBody,
  ErrorNotification,
  {
    organizationId: string;
    credentialId: number;
  }
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async payload => {
      const res = await axios.delete(
        `/api/organization/v1/${payload.organizationId}/assume-role-credentials/${payload.credentialId}`
      );
      return res.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          `/api/organization/v1/${variables.organizationId}/assume-role-credentials`,
        ],
      });
      setNotification({
        id: "org-assume-role-delete-success",
        message: "Assume-role credential deleted",
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "org-assume-role-delete-failed",
        message: "Failed to delete assume-role credential",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const ORG_GOOGLE_USAGE_SCOPE_OPTIONS = [
  { label: "Dataset", value: GoogleCredentialUsageScope.Dataset },
  // { label: "Run", value: GoogleCredentialUsageScope.Run },
  // { label: "Cortex", value: GoogleCredentialUsageScope.Cortex },
] as const;
